import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import { Search, SlidersHorizontal, XCircle } from "lucide-react";

import { api } from "@/lib/api";
import { FIELDS } from "@/lib/options";
import { StyleGridCard } from "@/components/cards";
import { Button, Chip, OptionRow, Spinner } from "@/components/kit";
import { Page } from "@/components/layout";
import HorizontalCarousel from "@/components/HorizontalCarousel";
import { useApp } from "@/store";

const CATEGORIES = ["For you", "All", "Straight", "Wavy", "Curly", "Coily", "Short", "Medium", "Long", "Low-Maintenance", "Trending", "Barber-Inspired"];
const FILTER_DEFS = [
  { key: "hair_type", label: "Hair type", options: FIELDS.hair_type.options },
  { key: "hair_pattern", label: "Hair pattern", options: FIELDS.hair_pattern.options },
  { key: "face_shape", label: "Face shape", options: FIELDS.face_shape.options },
  { key: "length", label: "Length", options: [{ v: "short", l: "Short" }, { v: "medium", l: "Medium" }, { v: "long", l: "Long" }] },
  { key: "hold", label: "Hold", options: FIELDS.hold.options },
  { key: "finish", label: "Finish", options: FIELDS.finish.options },
  { key: "maintenance", label: "Maintenance", options: [{ v: "low", l: "Low" }, { v: "medium", l: "Medium" }, { v: "high", l: "High" }] },
  { key: "difficulty", label: "Styling difficulty", options: [{ v: "easy", l: "Easy" }, { v: "medium", l: "Medium" }, { v: "advanced", l: "Advanced" }] },
];

function FilterSheet({ open, filters, onChange, onClose, onClear, count }) {
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay data-testid="filter-sheet-backdrop" className="animate-fade-in fixed inset-0 z-50 bg-[rgba(17,24,39,0.45)]" />
        <Dialog.Content data-testid="filter-sheet" className="animate-fade-up fixed inset-x-0 bottom-0 z-50 flex max-h-[85vh] flex-col rounded-t-[20px] bg-off pt-3 outline-none md:inset-y-0 md:left-auto md:right-0 md:max-h-none md:w-[440px] md:rounded-none md:rounded-l-[20px]">
          <div className="mx-auto mb-3 h-1 w-10 rounded bg-line md:hidden" />
          <div className="mb-3 flex items-center justify-between px-6">
            <Dialog.Title className="t-h2">Filters</Dialog.Title>
            <button type="button" data-testid="filter-sheet-clear" onClick={onClear} className="focus-ring min-h-[44px] rounded-md t-label !text-ocean">Clear all</button>
          </div>
          <Dialog.Description className="sr-only">Filter hairstyles by hair and style details</Dialog.Description>
          <div className="flex-1 space-y-4 overflow-y-auto px-6 pb-4">
            {FILTER_DEFS.map((d) => (
              <fieldset key={d.key} className="flex flex-col gap-2">
                <legend className="t-label mb-2">{d.label}</legend>
                <OptionRow
                  testPrefix={`filter-${d.key}`}
                  options={d.options}
                  value={filters[d.key]}
                  onChange={(v) => {
                    const next = { ...filters, [d.key]: v };
                    if (d.key === "hair_type" && v) next.hair_pattern = null;
                    if (d.key === "hair_pattern" && v) next.hair_type = null;
                    onChange(next);
                  }}
                />
              </fieldset>
            ))}
          </div>
          <div className="border-t border-divider px-6 py-3">
            <Button testId="filter-sheet-apply" title={count == null ? "Show styles" : `Show ${count} style${count === 1 ? "" : "s"}`} onClick={onClose} className="w-full" />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default function Explore() {
  const { topSlugs, hasProfile } = useApp();
  const [category, setCategory] = useState("All");
  const [filters, setFilters] = useState({});
  const [sheet, setSheet] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);
  const navigate = useNavigate();
  const activeFilters = Object.values(filters).filter(Boolean).length;

  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      try {
        setError(null);
        const params = new URLSearchParams({ q });
        Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
        if (category === "For you") params.set("slugs", [...topSlugs].join(",") || "none");
        else if (category !== "All") params.set("category", category);
        const r = await api(`/hairstyles?${params.toString()}`);
        if (alive) setItems(r);
      } catch (e) {
        if (alive) setError(e.message);
      }
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q, nonce, filters, category, topSlugs]);

  const clearAll = () => {
    setFilters({});
    setCategory("All");
    setQ("");
  };
  const anyActive = activeFilters > 0 || category !== "All" || q.length > 0;

  return (
    <div data-testid="explore-screen">
      <div className="sticky top-0 z-20 border-b border-divider bg-off/95 backdrop-blur md:top-16">
        <Page wide className="flex flex-col gap-2 pt-5">
          <p className="t-label">Hairstyle library</p>
          <h1 className="t-h1">EXPLORE.</h1>
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <label className="flex h-12 flex-1 items-center gap-2 rounded-full border border-line bg-white px-4 focus-within:border-ocean focus-within:ring-2 focus-within:ring-ocean/30">
              <Search className="h-[18px] w-[18px] text-mute" aria-hidden />
              <span className="sr-only">Search hairstyles</span>
              <input data-testid="explore-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search fades, curls, middle part…" className="h-12 flex-1 bg-transparent font-body-medium text-[15px] text-ink outline-none placeholder:text-mute" />
              {q ? <button type="button" data-testid="explore-search-clear" aria-label="Clear search" onClick={() => setQ("")} className="focus-ring rounded-full"><XCircle className="h-[18px] w-[18px] text-mute" /></button> : null}
            </label>
            <div className="flex items-center gap-3">
              <button type="button" data-testid="explore-filters-button" onClick={() => setSheet(true)} className={`focus-ring flex h-11 items-center gap-1.5 rounded-full border-[1.5px] border-navy px-4 font-body-bold text-[13px] transition-colors ${activeFilters ? "bg-navy text-white" : "text-navy hover:bg-navy/5"}`}>
                <SlidersHorizontal className="h-[18px] w-[18px]" aria-hidden /> Filters{activeFilters ? ` · ${activeFilters}` : ""}
              </button>
              <span data-testid="explore-count" className="t-caption flex-1 md:flex-none">{items ? `${items.length} style${items.length === 1 ? "" : "s"}` : " "}</span>
              {anyActive ? <button type="button" data-testid="explore-clear-all" onClick={clearAll} className="focus-ring min-h-[44px] rounded-md t-label !text-ocean">Clear all</button> : null}
            </div>
          </div>
          <HorizontalCarousel testId="explore-categories" ariaLabel="Categories" arrows={false} rowClassName="-mx-5 h-[60px] items-center gap-2 px-5 scroll-px-5 md:mx-0 md:px-0 md:scroll-px-0">
            {CATEGORIES.filter((c) => c !== "For you" || hasProfile).map((c) => (
              <Chip key={c} testId={`explore-category-${c.toLowerCase().replace(/ /g, "-")}`} label={c} selected={category === c} onClick={() => setCategory(c)} />
            ))}
          </HorizontalCarousel>
        </Page>
      </div>
      <Page wide className="py-5">
        {error ? (
          <div className="flex flex-col items-center gap-3 py-12">
            <p data-testid="explore-error" className="t-body">{error}</p>
            <Button testId="explore-retry" title="Retry" variant="outline" onClick={() => setNonce((n) => n + 1)} />
          </div>
        ) : !items ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : !items.length ? (
          <div className="flex flex-col items-center gap-3 py-12">
            <p data-testid="explore-empty" className="t-body">No styles match these filters.</p>
            <Button testId="explore-empty-clear" title="Clear all" variant="outline" onClick={clearAll} />
          </div>
        ) : (
          <div data-testid="explore-list" className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {items.map((item) => (
              <StyleGridCard key={item.slug} testId={`explore-style-${item.slug}`} style={item} forYou={topSlugs.has(item.slug)} onClick={() => navigate(`/style/${item.slug}`)} />
            ))}
          </div>
        )}
      </Page>
      <FilterSheet open={sheet} filters={filters} onChange={setFilters} onClose={() => setSheet(false)} onClear={() => setFilters({})} count={items?.length ?? null} />
    </div>
  );
}
